import { ChevronDown, Heart, Play, SkipBack, SkipForward } from "lucide-react";

const BUBBLE_CLASS = {
  green: "bg-[#3d8b3d] text-white border-[#5aad5a]",
  orange: "bg-[#c87e1e] text-white border-[#e8a030]",
  gray: "bg-[#6b6f76] text-white border-[#888c94]",
  violet: "bg-[#4c4b78] text-[#d7d5f8] border-[#626199]",
};

const BADGE_CLASS = {
  blind: "bg-[#3a6278] text-[#b8dce8]",
  mid: "bg-[#5a5e66] text-[#d4d6da]",
  late: "bg-[#5a5e66] text-[#d4d6da]",
};

export function NativeSceneRenderer({ scene }) {
  if (!scene) return null;
  return (
    <div
      className="overflow-hidden rounded-lg border border-[#202331] bg-[#171823] text-[#e8e8ec] shadow-[0_18px_45px_rgba(0,0,0,0.35)]"
      data-testid="native-scene-renderer"
      data-native-source={scene.source || "unknown"}
    >
      <SceneHeader header={scene.header} />
      <div data-testid="native-scene-sections">
        {(scene.sections || []).map((section, index) => {
          if (section.type === "pot_summary") return <PotSummary key={`pot-${index}`} section={section} />;
          return <StreetSection key={section.key || index} section={section} />;
        })}
      </div>
      <SceneFooter footer={scene.footer} />
    </div>
  );
}

function SceneHeader({ header = {} }) {
  return (
    <header className="border-b border-[#252837] bg-[#181924] px-4 py-2.5" data-testid="native-scene-header">
      <div className="flex items-center gap-4 text-[15px] font-semibold tracking-wide text-[#d0d0d6]">
        <span data-testid="native-scene-date">{header.date || "—"}</span>
        <span data-testid="native-scene-blinds">⚙ {header.blinds || "—"}</span>
      </div>
      <div className="mt-1 font-mono text-[13px] font-medium tracking-wide text-[#606378]" data-testid="native-scene-hand-id">
        {header.handId || "—"}
      </div>
    </header>
  );
}

function StreetSection({ section }) {
  return (
    <section className="border-b border-[#232635]" data-testid={`native-scene-street-${section.stage}`}>
      {/* Street header: label | cards | pot */}
      <div
        className="grid grid-cols-[64px_1fr_auto] items-center bg-[#1a1b27] pl-2 pr-3 py-2"
        data-testid={`native-scene-street-header-${section.stage}`}
      >
        <h3 className="text-[16px] font-bold leading-none text-[#e8e8eb]">{section.title}</h3>
        <CardRows rows={section.cardRows || []} stage={section.stage} />
        <div className="pl-3 text-right text-[16px] font-bold tabular-nums text-[#e0e0e4]" data-testid={`native-scene-pot-${section.stage}`}>
          {section.pot || ""}
        </div>
      </div>
      {/* Action rows below street header */}
      {(section.rows || []).length > 0 && (
        <div
          className="bg-[#151621] pl-2 pr-3 py-1"
          data-testid={`native-scene-action-rows-${section.stage}`}
          data-layout="full-width-under-stage-not-card-side"
        >
          {section.rows.map((row, index) => (
            <ActionRow key={row.key || index} row={row} index={index} stage={section.stage} />
          ))}
        </div>
      )}
    </section>
  );
}

function CardRows({ rows, stage }) {
  return (
    <div className="flex min-w-0 flex-col items-center justify-center gap-0.5" data-testid={`native-scene-card-rows-${stage}`}>
      {rows.map((cards, rowIndex) => (
        <div className="flex items-center justify-center gap-1" key={`row-${rowIndex}`} data-testid={`native-scene-card-row-${stage}-${rowIndex}`}>
          {cards.map((card, index) => (
            <SceneCard key={`${rowIndex}-${index}`} card={card} index={index} stage={stage} />
          ))}
        </div>
      ))}
    </div>
  );
}

function SceneCard({ card, index, stage }) {
  const src = card?.spritePath?.startsWith("/api/")
    ? card.spritePath
    : card?.spritePath?.startsWith("/native-ui/")
    ? `/api/v2${card.spritePath}`
    : card?.spritePath;
  return (
    <img
      src={`${process.env.REACT_APP_BACKEND_URL}${src || "/api/v2/native-ui/cards/back.png"}`}
      alt={card?.faceDown ? "card back" : card?.label || "card"}
      className="h-[36px] w-[27px] rounded-[2px] object-contain shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
      data-testid={`native-scene-card-${stage}-${index}`}
    />
  );
}

function ActionRow({ row, stage, index }) {
  const badgeTone = BADGE_CLASS[row.positionTone] || BADGE_CLASS.mid;
  return (
    <div
      className="grid min-h-[32px] grid-cols-[42px_1fr_28px_60px_72px] items-center gap-x-2 py-[3px] text-[14px] leading-tight"
      data-testid={`native-scene-action-row-${stage}-${index}`}
      data-layout="native-action-full-row"
    >
      <span
        className={`inline-flex h-[18px] items-center justify-center rounded-[3px] px-1 text-[10px] font-bold ${badgeTone}`}
        data-testid={`native-scene-position-${stage}-${index}`}
      >
        {row.position}
      </span>
      <span className="truncate font-medium text-[#e4e4e8]" data-testid={`native-scene-nick-${stage}-${index}`}>{row.nick}</span>
      <ActionBubble bubble={row.bubble} stage={stage} index={index} />
      <span className="text-right tabular-nums font-medium text-[#e0e0e4]" data-testid={`native-scene-amount-${stage}-${index}`}>{row.amount || ""}</span>
      <span className="text-right tabular-nums font-medium text-[#b8b8c0]" data-testid={`native-scene-stack-${stage}-${index}`}>{row.stack}</span>
    </div>
  );
}

function ActionBubble({ bubble = {}, stage, index }) {
  const tone = BUBBLE_CLASS[bubble.tone] || BUBBLE_CLASS.violet;
  return (
    <span
      className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border text-[12px] font-bold shadow-[0_1px_2px_rgba(0,0,0,0.35)] ${tone}`}
      data-testid={`native-scene-action-bubble-${stage}-${index}`}
      data-action-category={bubble.category || "unknown"}
    >
      {bubble.label}
    </span>
  );
}

function PotSummary({ section }) {
  return (
    <div className="grid grid-cols-[80px_1fr] items-center border-b border-[#232635] bg-[#181924] px-3 py-2" data-testid="native-scene-pot-summary">
      <span className="text-[16px] font-bold text-[#e8e8eb]">{section.title}</span>
      <span className="text-[14px] font-medium text-[#c0c0c8]" data-testid="native-scene-pot-summary-text">
        {(section.items || []).map((item) => `${item.label}(${item.amount})`).join("   ")}
      </span>
    </div>
  );
}

function SceneFooter({ footer = {} }) {
  return (
    <footer className="border-t border-[#2c2d3b] bg-[#1e1d2a]" data-testid="native-scene-footer">
      <div className="grid grid-cols-2 text-center text-[14px] font-semibold">
        <div className="bg-[#2a2938] py-2.5 text-[#c8c6d4]" data-testid="native-scene-summary-tab">Resumen de la mano</div>
        <div className="py-2.5 text-[#19c888]" data-testid="native-scene-detail-tab">{footer.activeTab || "Detalles de mano"}</div>
      </div>
      <div className="grid grid-cols-5 items-center px-4 py-2.5 text-center text-[#15c785]">
        <div className="flex flex-col items-center gap-0.5 text-white" data-testid="native-scene-favorite"><Heart className="h-5 w-5" /> <span className="text-[12px] font-medium">{footer.leftLabel || "—"}</span></div>
        <SkipBack className="mx-auto h-6 w-6 fill-current" data-testid="native-scene-prev" />
        <span className="text-[16px] font-bold text-white" data-testid="native-scene-counter">{footer.centerLabel || "—/—"}</span>
        <Play className="mx-auto h-6 w-6 fill-current" data-testid="native-scene-play" />
        <ChevronDown className="mx-auto h-7 w-7 rotate-180 stroke-[3]" data-testid="native-scene-jump" />
      </div>
    </footer>
  );
}
